import { useEffect, useState } from "react";
import { ArrowRight, Check, Phone, X } from "lucide-react";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import CalendlyModal from "@/components/CalendlyModal";
import ExitIntentPopup from "@/components/ExitIntentPopup";
import { Analytics } from "@/lib/analytics";
import { signupUrl } from "@/lib/productApp";
import SEOHead from "@/components/SEOHead";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PLAN_AMOUNTS, USAGE_PRICE_EUR } from "@/lib/pricing";

const plans = [
  {
    id: "solo" as const,
    name: "Solo",
    subtitle: "Pour les indépendants",
    features: [
      ["1 utilisateur", true],
      ["1 numéro", true],
      ["Qualification des appels sur mesure", true],
      ["Dashboard, transcriptions & résumés", true],
      ["Prise de rendez-vous & agenda", true],
      ["Alertes email", true],
      ["Alertes SMS", false],
      ["Zapier", false],
      ["Assistant Immovox dans ChatGPT", true],
      ["Onboarding autonome", true],
      ["Support email", true],
    ] as const,
  },
  {
    id: "pro" as const,
    name: "Pro",
    subtitle: "Pour les équipes",
    popular: true,
    features: [
      ["Jusqu'à 5 utilisateurs", true],
      ["1 numéro", true],
      ["Qualification des appels sur mesure", true],
      ["Dashboard, transcriptions & résumés", true],
      ["Prise de rendez-vous & agenda", true],
      ["Alertes email", true],
      ["Alertes SMS", true],
      ["Zapier", true],
      ["Assistant Immovox dans ChatGPT", true],
      ["Onboarding autonome ou accompagné", true],
      ["Support email prioritaire", true],
    ] as const,
  },
  {
    id: "business" as const,
    name: "Business",
    subtitle: "Sur mesure",
    features: [
      ["Utilisateurs illimités", true],
      ["Numéros sur devis", true],
      ["Qualification des appels sur mesure", true],
      ["Dashboard, transcriptions & résumés", true],
      ["Prise de rendez-vous & agenda", true],
      ["Alertes email", true],
      ["Alertes SMS", true],
      ["Zapier", true],
      ["Assistant Immovox dans ChatGPT", true],
      ["Accompagnement dédié", true],
      ["Account manager dédié", true],
    ] as const,
  },
];

const pricingFaqs = [
  {
    question: "Comment fonctionne la facturation ?",
    answer: "Choisissez votre plan : Solo (89€/mois HT), Pro (179€/mois HT) ou Business (249€/mois HT). À cela s'ajoutent 0,19€/min de conversation avec l'assistant, selon l'usage réel. Aucun pack de minutes à prépayer.",
  },
  {
    question: "Quelle est la différence entre les plans ?",
    answer: "Solo couvre un utilisateur et un numéro. Pro ajoute jusqu'à 5 utilisateurs, les alertes SMS, Zapier et un support prioritaire. Business étend l'organisation avec des utilisateurs illimités, des numéros sur devis et un accompagnement dédié.",
  },
  {
    question: "Puis-je essayer avant de m'engager ?",
    answer: "Oui. Solo et Pro incluent un essai gratuit de 7 jours sans carte bancaire. Vous pouvez configurer Immovox et le tester sur vos propres appels avant de choisir de poursuivre.",
  },
  {
    question: "Y a-t-il un engagement minimum ?",
    answer: "Non. Les abonnements sont sans engagement et peuvent être résiliés à tout moment.",
  },
  {
    question: "Comment sont facturées les minutes d'appel ?",
    answer: "Seul le temps réellement passé en conversation avec l'assistant est comptabilisé, puis cumulé sur le mois. Après un transfert vers votre ligne, la partie de conversation entre l'appelant et votre équipe n'est pas facturée comme temps Immovox.",
  },
  {
    question: "Que se passe-t-il si je dépasse mon volume habituel ?",
    answer: "Le volume d'appels ne déclenche pas automatiquement un changement de plan : l'usage supplémentaire reste facturé au même tarif minute. Le choix du plan dépend surtout des utilisateurs, fonctionnalités et besoins d'accompagnement.",
  },
];

export default function Tarifs() {
  const [calendlyOpen, setCalendlyOpen] = useState(false);

  useEffect(() => {
    Analytics.pricingViewed();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Tarifs Immovox : Solo 89€, Pro 179€, Business 249€"
        description="Tarifs Immovox : Solo 89€, Pro 179€, Business 249€/mois HT + 0,19€/min de conversation. Essai 7 jours sans carte bancaire, sans engagement."
        canonical="/tarifs"
      />
      <Header />

      <main>
        <section className="pt-32 pb-14">
          <div className="mx-auto max-w-[1344px] px-5 md:px-8">
            <div className="grid gap-8 lg:grid-cols-12">
              <div className="lg:col-span-8">
                <p className="eyebrow mb-6">Tarifs</p>
                <h1 className="text-4xl font-semibold tracking-[-.045em] md:text-6xl">Un plan pour chaque façon de travailler.</h1>
              </div>
              <div className="lg:col-span-4 lg:pt-12">
                <p className="text-[17px] leading-7 text-muted-foreground">
                  Choisissez le plan adapté à votre équipe, puis payez uniquement le temps de conversation réellement consommé.
                </p>
                <div className="mt-5 flex items-center gap-2 text-sm">
                  <Phone className="h-4 w-4 text-primary" />
                  {USAGE_PRICE_EUR} de conversation réelle
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="pb-16 md:pb-20">
          <div className="mx-auto max-w-[1120px] px-5 md:px-8">
            <div className="border-t border-foreground/20">
              {plans.map((plan, i) => (
                <div
                  key={plan.id}
                  className={
                    "grid gap-5 border-b border-border py-7 md:grid-cols-[60px_1fr_.8fr_1.5fr_auto] md:items-center " +
                    (plan.popular ? "bg-secondary/45 -mx-4 px-4 md:-mx-6 md:px-6" : "")
                  }
                >
                  <span className="font-mono text-[10px] text-primary">0{i + 1}</span>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-xl font-semibold">{plan.name}</h2>
                      {plan.popular && <span className="font-mono text-[9px] uppercase tracking-[.12em] text-primary">Recommandé</span>}
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">{plan.subtitle}</p>
                  </div>
                  <div>
                    <span className="text-3xl font-semibold">{PLAN_AMOUNTS[plan.id].EUR}€</span>
                    <span className="text-sm text-muted-foreground"> /mois HT</span>
                  </div>
                  <ul className="space-y-2 text-[13px] text-foreground/75">
                    {plan.features.slice(0, 6).map(([label, included]) => (
                      <li key={label} className={included ? "flex gap-2" : "flex gap-2 text-muted-foreground"}>
                        {included ? <Check className="mt-0.5 h-4 w-4 text-primary" /> : <X className="mt-0.5 h-4 w-4" />}
                        {label}
                      </li>
                    ))}
                  </ul>
                  {plan.id === "business" ? (
                    <button
                      onClick={() => {
                        Analytics.ctaClicked("contact_business", "tarifs_page");
                        setCalendlyOpen(true);
                      }}
                      className="inline-flex h-11 items-center justify-center gap-2 border border-foreground/20 px-4 text-sm font-semibold hover:border-foreground/45"
                    >
                      Nous contacter <ArrowRight className="h-4 w-4" />
                    </button>
                  ) : (
                    <a
                      href={signupUrl(plan.id)}
                      onClick={() => Analytics.ctaClicked("demarrer_essai_" + plan.id, "tarifs_page")}
                      className={
                        plan.popular
                          ? "inline-flex h-11 items-center justify-center gap-2 bg-primary px-4 text-sm font-semibold text-primary-foreground"
                          : "inline-flex h-11 items-center justify-center gap-2 border border-foreground/20 px-4 text-sm font-semibold"
                      }
                    >
                      Essayer gratuitement <ArrowRight className="h-4 w-4" />
                    </a>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-8 border-y border-border py-6">
              <div className="grid gap-4 text-sm text-muted-foreground sm:grid-cols-3">
                <span><b className="block text-foreground">7 jours gratuits</b>Sans carte bancaire</span>
                <span><b className="block text-foreground">Sans engagement</b>Résiliez quand vous voulez</span>
                <span><b className="block text-foreground">Même tarif d'usage</b>{USAGE_PRICE_EUR} sur tous les plans</span>
              </div>
            </div>

            <div className="mt-12">
              <p className="eyebrow mb-5">Comparatif complet</p>
              <div className="overflow-x-auto border-y border-foreground/20">
                <table className="w-full min-w-[760px] text-left">
                  <thead>
                    <tr>
                      <th className="px-4 py-4 text-sm font-semibold">Fonctionnalités</th>
                      {plans.map((plan) => <th key={plan.id} className="px-4 py-4 text-sm font-semibold">{plan.name}<span className="ml-2 font-normal text-muted-foreground">{PLAN_AMOUNTS[plan.id].EUR}€</span></th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["Utilisateurs","1","Jusqu'à 5","Illimités"],
                      ["Numéro inclus","1","1","Sur devis"],
                      ["Qualification sur mesure",true,true,true],
                      ["Dashboard, transcriptions & résumés",true,true,true],
                      ["Prise de rendez-vous & agenda",true,true,true],
                      ["Alertes email",true,true,true],
                      ["Alertes SMS",false,true,true],
                      ["Zapier",false,true,true],
                      ["Assistant Immovox dans ChatGPT",true,true,true],
                      ["Onboarding","Autonome","Autonome ou accompagné","Dédié"],
                      ["Support","Email","Prioritaire","Account manager dédié"],
                    ].map(([label,...values]) => (
                      <tr key={String(label)} className="border-t border-border">
                        <td className="px-4 py-4 text-[14px] font-medium">{label as string}</td>
                        {values.map((value, index) => (
                          <td key={index} className="px-4 py-4 text-[14px] text-foreground/75">
                            {value === true ? <Check className="h-4 w-4 text-primary" /> : value === false ? <span className="text-muted-foreground">—</span> : String(value)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-secondary/45 py-16 md:py-20">
          <div className="mx-auto max-w-[980px] px-5 md:px-8">
            <div className="grid gap-8 lg:grid-cols-12">
              <div className="lg:col-span-4">
                <p className="eyebrow">FAQ</p>
                <h2 className="mt-4 text-3xl font-semibold">Questions sur les tarifs</h2>
              </div>
              <Accordion type="single" collapsible className="border-t border-foreground/20 lg:col-span-7 lg:col-start-6">
                {pricingFaqs.map((faq, i) => (
                  <AccordionItem key={faq.question} value={"faq-" + i} className="border-border">
                    <AccordionTrigger className="py-5 text-left text-[15px] font-medium hover:no-underline hover:text-primary">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="pb-5 text-[14px] leading-7 text-muted-foreground">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <CalendlyModal open={calendlyOpen} onOpenChange={setCalendlyOpen} source="tarifs" />
      <ExitIntentPopup />
    </div>
  );
}
